import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { callQuery, GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE, GET_LATEST_VERSIONS_FOR_CSN, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PatientFileVersion, UNREAD_EXPLAIN_JOBS } from "../../../../amplify/appsync/query";
import { PatientVisit } from "../../../../amplify/appsync/search-patients";

import { faBolt, faRefresh, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { v4 as uuidv4 } from 'uuid';
import { callLLMDashboardStreamable } from "../../../../amplify/api/llm-chat";
import { getWSService } from "../../../../amplify/api/llm-web-socket";
import { callDashboardLLM, callGetDashboardLLMStatus, LLMDashboardStatus } from "../../../../amplify/appsync/dashboard-llm";
import { callWrite, EXPLAIN_ANATOMY, SCAN_MULTI, UPDATE_LAST_VIEWED } from "../../../../amplify/appsync/write";
import { useDashboardTrigger } from "../../../../context/DashboardTriggerContext";
import { getDaysBetweenDates } from "../../../../utils/time";
import useSetPagePatientVisit from "../../../common/GlobalOptionsContext";
import { dashboardLLMCache } from "../cache";
import { AdditionalContextModal } from "../common/AdditionalContextModal";
import { LLMDashboardUpdated } from "../common/LLMDashboardUpdated";
import './AnatomyDashboard.scss';
import { AnatomyFindingSection } from "./AnatomyFindingSection";
import { jsonrepair } from "jsonrepair";
import { getFileFromKey } from "../common/utils";
import { isSectionUnread } from "../utils";


export const JOB_CONFIG = [
    { key: "scan_multi", file: SCAN_MULTI },
    { key: "anatomy", file: EXPLAIN_ANATOMY },
];

let socketConnection: any;
let sessionId: string;

export const AnatomyDashboard = (props: {
    user: any,
    ws: string,
    tenant: string,
    patientVisit?: PatientVisit,
    unreadFileVersionMap?: Record<string, PatientFileVersion>,
    setUnreadFileVersionMap?: (map: Record<string, PatientFileVersion>) => void,
}) => {
    let { internalCSN } = useParams();
    const [statuses, setStatuses] = useState<Record<string, LLMDashboardStatus>>({});
    const patientVisit = props.patientVisit;
    useSetPagePatientVisit(patientVisit);

    const [expandedFinding, setExpandedFinding] = useState<string>("");

    const [feedbacks, setFeedbacks] = useState<LLMVisitFeedback[]>([]);
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);

    //
    // State variables for the scan multi popup
    //
    const [isScanMultiModalOpen, setIsScanMultiModalOpen] = useState(false);
    const [latestFileVersions, setLatestFileVersions] = useState<Record<string, PatientFileVersion>>();
    const [selectedScanMultiVersion, setSelectedScanMultiVersion] = useState<any>();
    const [selectedInitialSection, setSelectedInitialSection] = useState<string>();
    const [selectedTriggeringEvents, setSelectedTriggeringEvents] = useState<string[]>();

    //
    // State variables for the pre-computed data
    //
    const [scanMulti, setScanMulti] = useState<any>();
    const [anatomy, setAnatomy] = useState<any>();

    const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false);
    const [isLoadingScanMulti, setIsLoadingScanMulti] = useState<boolean>(false);
    const [isLoadingAnatomy, setIsLoadingAnatomy] = useState<boolean>(false);

    const [selectedCategories, setSelectedCategories] = useState<any>([]);
    const [dateRange, setDateRange] = useState({ min: 0, max: 0 });
    const [maxDaysAgo, setMaxDaysAgo] = useState(0);

    const ANATOMICAL_REGIONS = ["HEAD", "NECK", "CHEST", "ABDOMEN", "PELVIS", "SPINE", "LUE", "RUE", "LLE", "RLE", "UNCLASSIFIED"];

    const sectionToIsLoading: { [key: string]: boolean } = {
        status: isLoadingStatus,
        scan_multi: isLoadingScanMulti,
        anatomy: isLoadingAnatomy,
    };

    const sectionToSetIsLoading: { [key: string]: (val: any) => void } = {
        status: setIsLoadingStatus,
        scan_multi: setIsLoadingScanMulti,
        anatomy: setIsLoadingAnatomy,
    };

    const sectionToGetter: { [key: string]: any } = {
        scan_multi: scanMulti,
        anatomy: anatomy,
    };

    const sectionToSetter: { [key: string]: (val: any) => void } = {
        scan_multi: setScanMulti,
        anatomy: setAnatomy,
    };

    const [findings, setFindings] = useState<any>([
        // {
        //     'region': 'RUE',
        //     'finding_text': 'Comminuted and impacted distal radial fracture',
        //     'study': 'XR FOREARM 2 VIEWS RIGHT',
        //     'result_time': '2021-01-12T17:09:35Z',
        //     'quote': 'Likely comminuted and impacted distal radial fracture, which may be subacute.',
        //     'category': 'Acute traumatic injury',
        //     'days_ago': 10,
        //     'systems': []
        // },
    ]);

    const [isLoading, setIsLoading] = useState(false);

    const regenerateKeyRef = useRef<any>();
    const [isWebSocketConnecting, setIsWebSocketConnecting] = useState<any>(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateTarget, setRegenerateTarget] = useState<string | undefined>();

    const { triggerAction, setTriggerAction } = useDashboardTrigger();

    const loadPrecomputedExplanations = async (patient: PatientVisit) => {
        const unavailableExplanations: string[] = [];

        const sections = JOB_CONFIG;

        for (let section of sections) {
            sectionToSetIsLoading[section.key]?.(true);
            const cachedCurrentVisit = await dashboardLLMCache.get(section.file, patient, props.tenant);
            if (cachedCurrentVisit) {
                sectionToSetter[section.key]?.(cachedCurrentVisit);
            } else {
                unavailableExplanations.push();
            }
            sectionToSetIsLoading[section.key]?.(false);
        }
    };

    useEffect(() => {
        if (!patientVisit) return;

        loadPrecomputedExplanations(patientVisit);

        callGetDashboardLLMStatus(patientVisit.csn).then((statuses) => {
            setStatuses(statuses);
        });

        callQuery({
            operation: GET_LATEST_VERSIONS_FOR_CSN,
            csn: patientVisit.csn,
        }).then((latestVersions: PatientFileVersion[]) => {
            const fileTypeToVersion: any = {};
            for (let latestVersion of latestVersions) {
                fileTypeToVersion[latestVersion.file_type] = latestVersion;
            }
            setLatestFileVersions(fileTypeToVersion);
        });

        callQuery({
            operation: LLM_VISIT_FEEDBACK_BY_CSN,
            csn: patientVisit.csn,
        }).then(feedbacks => {
            setFeedbacks(feedbacks);
        });
    }, [patientVisit]);

    useEffect(() => {
        if (!patientVisit) return;
        if (!props.unreadFileVersionMap) return;

        callQuery({
            operation: UNREAD_EXPLAIN_JOBS,
            csn: patientVisit.csn,
        }).then((unreadFileVersions: PatientFileVersion[]) => {
            const fileVersionMap: Record<string, PatientFileVersion> = {};
            for (let fv of unreadFileVersions) {
                fileVersionMap[fv.file_type] = fv;
            }
            props.setUnreadFileVersionMap?.(fileVersionMap);

            callWrite({
                operation: UPDATE_LAST_VIEWED,
                csn: patientVisit.csn,
                jobs: JOB_CONFIG.map(config => config.file),
            }).then((result: any) => {
                console.log("Completed UPDATE_LAST_VIEWED");
            });
        });
    }, [patientVisit]);

    useEffect(() => {
        try {
            if (findings) {
                // Find maximum days_ago value and set date range max (min is fixed at 0)
                const maxDays = Math.max(...findings.map((f: any) => f.days_ago), 0);
                setMaxDaysAgo(maxDays);
                setDateRange({ min: 0, max: maxDays }); // Set initial max to the oldest finding

                // Set initial selected categories
                const uniqueCategories = new Set();
                for (let finding of findings) {
                    uniqueCategories.add(finding.category);
                }
                setSelectedCategories(Array.from(uniqueCategories));
            }
        } catch (error) {
            console.error('Error loading data:', error);
        }
    }, [findings]);

    useEffect(() => {
        if (anatomy && anatomy.findings) {
            setFindings((prev: any) => {

                const findings = anatomy["findings"];
                for (let finding of findings) {
                    if (patientVisit) {
                        finding.days_ago = getDaysBetweenDates(finding.result_time, patientVisit.arrival_time);
                    }
                }
                // Sort findings by anatomical region order
                const sortedFindings = findings.sort((a: any, b: any) => {
                    const aIndex = ANATOMICAL_REGIONS.indexOf(a.region);
                    const bIndex = ANATOMICAL_REGIONS.indexOf(b.region);
                    return aIndex - bIndex;
                });

                return sortedFindings;
            });
        }
    }, [anatomy]);

    const getCategoryColor = (category: any) => {
        const colorMap: any = {
            'Acute traumatic injury': '#ff6b6b',
            'Chronic disease': '#ffa94d',
            'Post-surgical or treatment-related changes': '#74c0fc',
            'Procedural or medical device issue': '#a9e34b',
            'No significant findings': '#69db7c',
            'Metastatic disease': '#cc5de8',
            'Significant incidental finding': '#ff922b',
            'Acute non-traumatic condition': '#4dabf7',
            'Infectious process': '#e599f7',
            'Indeterminate finding': '#868e96',
            'Developmental or congenital anomaly': '#3bc9db'
        };

        return colorMap[category] || '#ced4da';
    };

    const toggleExpandedFinding = (expandedKey: string) => {
        if (expandedFinding === expandedKey) {
            setExpandedFinding("");
        } else {
            setExpandedFinding(expandedKey);
        }
    };

    const getRegionPosition = (region: any, finding: any) => {
        // Base positions for anatomical regions
        const basePositions: any = {
            'HEAD': { top: '10%', left: '50%' },
            'NECK': { top: '18%', left: '50%' },
            'CHEST': { top: '25%', left: '50%' },
            'ABDOMEN': { top: '40%', left: '50%' },
            'PELVIS': { top: '50%', left: '50%' },
            'SPINE': { top: '35%', left: '50%' },
            'RUE': { top: '27%', left: '37%' }, // Viewer's left (patient's right)
            'LUE': { top: '27%', left: '63%' }, // Viewer's right (patient's left)
            'RLE': { top: '65%', left: '45%' }, // Viewer's left (patient's right)
            'LLE': { top: '65%', left: '55%' }  // Viewer's right (patient's left)
        };

        // Special case adjustments for specific findings
        if (finding) {
            if (region === 'RUE' && finding.finding_text && finding.finding_text.includes('hand')) {
                return { top: '21%', left: '32%' };
            }

            if (region === 'RUE' && finding.finding_text && finding.finding_text.includes('radial')) {
                return { top: '24%', left: '35%' };
            }

            if (region === 'LLE' && finding.finding_text && finding.finding_text.includes('foot')) {
                return { top: '80%', left: '53%' };
            }
        }

        return basePositions[region] || { top: '50%', left: '50%' };
    };

    // Get unique categories present in the data
    const uniqueCategoriesInData = useMemo(() => {
        const uniqueCategories = new Set();
        if (findings) {
            for (let finding of findings) {
                uniqueCategories.add(finding.category);
            }
        }
        return Array.from(uniqueCategories);
    }, [findings]);

    // Filter findings based on selected categories and max days_ago
    const filteredFindings = useMemo(() => {
        return findings.filter((finding: any) =>
            selectedCategories.includes(finding.category) &&
            finding.days_ago >= 0 && // min is always 0
            finding.days_ago <= dateRange.max // Only filter by max
        );
    }, [findings, selectedCategories, dateRange.max]);

    // Toggle category selection
    const toggleCategory = (category: any) => {
        if (selectedCategories.includes(category)) {
            setSelectedCategories(selectedCategories.filter((c: any) => c !== category));
        } else {
            setSelectedCategories([...selectedCategories, category]);
        }
    };

    // Update the ref whenever regenerateTarget changes
    useEffect(() => {
        let config = null;
        for (let i = 0; i < JOB_CONFIG.length; i++) {
            if (regenerateTarget && JOB_CONFIG[i].file !== regenerateTarget) {
                continue;
            }
            config = JOB_CONFIG[i];
        }
        regenerateKeyRef.current = config?.key;
    }, [regenerateTarget]);

    // Store accumulated text in a ref so we don't trigger extra re-renders
    const accumulatedTextRef = useRef("");
    const lastSeqRef = useRef(0);

    const handleChatUpdate = useCallback((response: any) => {
        if (
            response &&
            typeof response === "object" &&
            !Array.isArray(response) &&
            (response["role"] === "COMPLETED" || response["role"] === "COMPLETED_WITH_ERROR")
        ) {
            // End of stream cleanup
            if (props.patientVisit) {
                callQuery({
                    operation: GET_LATEST_VERSIONS_FOR_CSN,
                    csn: props.patientVisit.csn,
                }).then((latestVersions: PatientFileVersion[]) => {
                    const fileTypeToVersion: any = {};
                    for (let latestVersion of latestVersions) {
                        fileTypeToVersion[latestVersion.file_type] = latestVersion;
                    }
                    setLatestFileVersions(fileTypeToVersion);
                });
                dashboardLLMCache.get(getFileFromKey(JOB_CONFIG, regenerateKeyRef.current), props.patientVisit, props.tenant).then((cachedCurrentVisit) => {
                    sectionToSetter[regenerateKeyRef.current]?.(cachedCurrentVisit);
                    sectionToSetIsLoading[regenerateKeyRef.current](false);
                });
                callGetDashboardLLMStatus(props.patientVisit.csn).then((statuses) => {
                    setStatuses(statuses);
                });
            }
        }
        // Oct 2025: Disable streaming because time-to-first token is high for Azure OpenAI models
        //           which leads to a degraded/non-existent stream. Re-enable once we move back to 
        //           native OpenAI or Claude models.
        //
        // else if (response && typeof response === "object" && "seq" in response && "delta" in response) {
        //     const { seq, delta } = response;

        //     // Optional: Check for skipped or out-of-order chunks
        //     if (seq !== lastSeqRef.current + 1) {
        //         console.warn(`⚠ Out of order chunk: expected ${lastSeqRef.current + 1} but got ${seq}`);
        //         // Optionally handle gap recovery here
        //     }

        //     // Append the new delta to accumulated text
        //     accumulatedTextRef.current += delta;
        //     lastSeqRef.current = seq;

        //     // Push the updated text into your UI
        //     if (regenerateKeyRef.current) {
        //         const key: any = regenerateKeyRef.current;
        //         sectionToSetIsLoading[key](false);
        //         try {
        //             sectionToSetter[key](JSON.parse(jsonrepair(accumulatedTextRef.current)));
        //         } catch {
        //             console.warn(`Could not parse the following accumulated text: ${accumulatedTextRef.current}`);
        //             sectionToSetter[key]({});
        //         }
        //     }
        // }
        // else if (regenerateKeyRef.current) {
        //     // Fallback: if the backend sends a plain string (legacy mode)
        //     const key: any = regenerateKeyRef.current;
        //     sectionToSetIsLoading[key](false);
        //     sectionToSetter[key](response);
        // }
    }, [regenerateTarget]);

    const handleWebSocketConnected = useCallback(() => {
        setIsWebSocketConnecting(false);
    }, []);

    const connect = () => {
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;

        setIsWebSocketConnecting(true);
        sessionId = uuidv4();
        socketConnection = getWSService(props.ws, sessionId, handleWebSocketConnected);
        if (socketConnection.sessionId) {
            // This is because getWSService may actually return a previous open web socket with an older connection ID
            sessionId = socketConnection.sessionId;
        }

        if (!socketConnection.hasListener(handleChatUpdate)) {
            socketConnection.addMessageListener(handleChatUpdate);
        }
    }

    const regenerate = async (shouldSyncData: boolean, target?: string) => {
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;
        if (patientVisit) {
            setIsRegenerating(true);

            let config = null;

            for (let i = 0; i < JOB_CONFIG.length; i++) {
                if (target && JOB_CONFIG[i].file !== target) {
                    continue;
                }
                if (JOB_CONFIG[i].file.startsWith("scan_")) {
                    continue;
                }
                config = JOB_CONFIG[i];
            }

            connect();

            if (config) {
                sectionToSetIsLoading[config.key]?.(true);
            }

            if (shouldSyncData) {
                const scanMultiResponse = await callDashboardLLM({
                    "operation": SCAN_MULTI,
                    "mrn": patientVisit.mrn,
                    "csn": patientVisit.csn,
                });
            }
            if (config) {
                await callLLMDashboardStreamable(sessionId, {
                    operation: config.file,
                    mrn: patientVisit.mrn,
                    csn: patientVisit.csn,
                    report_date_str: new Date().toISOString().split('.')[0] + 'Z',
                });
            }
            setIsRegenerating(false);
        }
    };

    useEffect(() => {
        if (triggerAction && triggerAction.name === 'anatomy') {
            regenerate(true);
            // Important: Reset the trigger action to avoid re-triggering
            setTriggerAction(null);
        }
    }, [triggerAction, regenerate, setTriggerAction]);

    /**
     * Called when the user wants to see the data snapshot for the LLM section
     */
    const handleShowScanMultiModal = (patientFileVersion?: PatientFileVersion) => {
        if (patientFileVersion) {
            callQuery({
                operation: GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE,
                csn: patientFileVersion.csn,
                file_type: 'scan_multi',
            })
            .then((versions: PatientFileVersion[]) => {
                // Find previous version
                const sortedVersions = [...versions].sort((a, b) => new Date(a.version_date).getTime() - new Date(b.version_date).getTime());
                const selectedIndex = sortedVersions.findIndex(v => v.version_id === patientFileVersion.source_version_id);
                const previousVersion = selectedIndex > 0 ? sortedVersions[selectedIndex - 1] : null;

                setSelectedScanMultiVersion({
                    origVersion: previousVersion ? previousVersion.source_version_id : undefined,
                    newVersion: patientFileVersion.source_version_id,
                });
            });
            setSelectedTriggeringEvents(patientFileVersion.source_changed_keys);
            setIsScanMultiModalOpen(true);
        }
    };

    /**
     * Called when the user selects another version of the LLM section
     */
    const handleFileVersionChange = (fileType: string, fileVersion: PatientFileVersion, previousFileVersion: PatientFileVersion | null) => {
        const updatedFileVersions = {
            ...latestFileVersions,
            [fileType]: {
                ...fileVersion
            },
        };
        setSelectedScanMultiVersion({
            origVersion: previousFileVersion ? previousFileVersion.source_version_id : undefined,
            newVersion: fileVersion.source_version_id,
        })
        setLatestFileVersions(updatedFileVersions);
    }

    return (
        <Container className="notes-dashboard">
            <Row className="mb-3">
                <Col>
                    <span className="gradient-text" style={{fontSize: '1.25rem'}}>Anatomy Explanation</span>{isSectionUnread(EXPLAIN_ANATOMY, props.unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                </Col>
                <Col className="text-right">
                    {
                        patientVisit && (
                            <>
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                        <Tooltip>
                                            Refresh Section
                                        </Tooltip>
                                    }
                                >
                                    <Button
                                        variant="primary"
                                        className="dashboard-round-btn"
                                        onClick={(e: any) => {e.stopPropagation(); setRegenerateTarget(EXPLAIN_ANATOMY); setShowContextMenu(true)}}
                                        disabled={isRegenerating}
                                    >
                                        {
                                            isRegenerating ? (
                                                <FontAwesomeIcon icon={faRefresh} spin />
                                            ) : (
                                                <FontAwesomeIcon icon={faRefresh} />
                                            )
                                        }
                                    </Button>
                                </OverlayTrigger>
                                <AdditionalContextModal regenerate={regenerate} showModal={showContextMenu} setShowModal={setShowContextMenu} patientVisit={patientVisit} target={regenerateTarget} tenant={props.tenant} />
                            </>
                        )
                    }
                </Col>
            </Row>

            {/* Category filters */}
            <div className="mt-4 mb-4">
                <div className="mb-2"><strong>Finding Categories</strong></div>
                <div className="d-flex flex-wrap gap-2">
                    {uniqueCategoriesInData.map((category: any) => (
                        <Button
                            key={category}
                            variant={selectedCategories.includes(category) ? "primary" : "light"}
                            size="sm"
                            className="rounded-pill d-flex align-items-center"
                            style={{
                                backgroundColor: selectedCategories.includes(category)
                                    ? getCategoryColor(category)
                                    : '#f1f3f5',
                                borderColor: selectedCategories.includes(category)
                                    ? getCategoryColor(category)
                                    : '#f1f3f5',
                            }}
                            onClick={() => toggleCategory(category)}
                        >
                            <div
                                className="rounded-circle me-1"
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    backgroundColor: selectedCategories.includes(category)
                                        ? '#ffffff'
                                        : getCategoryColor(category)
                                }}
                            ></div>
                            {category}
                        </Button>
                    ))}
                </div>
            </div>

            {/* Date Range Slider */}
            <div className="mb-4">
                <div className="mb-2"><strong>Oldest Finding Date (Days Ago)</strong></div>
                <div className="px-2">
                    <div className="d-flex justify-content-between small text-muted mb-1">
                        <span>{maxDaysAgo > 0 ? `${maxDaysAgo} (Oldest)` : "Oldest"} days ago</span>
                        <span>0 days ago (Newest)</span>
                    </div>

                    <div className="position-relative mb-4 pt-4" style={{ height: '40px' }}>
                        {/* Background Track */}
                        <div
                            className="position-absolute bg-secondary rounded w-100"
                            style={{
                                height: '4px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                opacity: 0.3,
                                zIndex: 0
                            }}
                        ></div>

                        {(() => {
                            // Calculate slider position
                            const sliderPositionValue = maxDaysAgo - dateRange.max;
                            const thumbPosPercent = maxDaysAgo === 0 ? 0 : (sliderPositionValue / maxDaysAgo) * 100;

                            return (
                                <>
                                    {/* Active Track */}
                                    <div
                                        className="position-absolute bg-primary rounded"
                                        style={{
                                            height: '4px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            left: `${thumbPosPercent}%`,
                                            right: '0%',
                                            zIndex: 1
                                        }}
                                    ></div>

                                    {/* Range Input */}
                                    <Form.Range
                                        min={0}
                                        max={maxDaysAgo}
                                        step={1}
                                        value={sliderPositionValue}
                                        onChange={(e) => {
                                            const newSliderPositionValue = parseInt(e.target.value);
                                            setDateRange(prev => ({
                                                ...prev,
                                                max: maxDaysAgo - newSliderPositionValue
                                            }));
                                        }}
                                        className="position-absolute w-100 h-100 top-0 left-0"
                                        style={{
                                            opacity: 0,
                                            pointerEvents: "auto",
                                            zIndex: 10
                                        }}
                                    />

                                    {/* Visual Thumb */}
                                    <div
                                        className="position-absolute bg-white border border-2 border-primary rounded-circle shadow"
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                            left: `${thumbPosPercent}%`,
                                            top: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            pointerEvents: 'none',
                                            zIndex: 30
                                        }}
                                    ></div>
                                </>
                            );
                        })()}
                    </div>

                    <div className="mt-2 bg-light p-3 rounded shadow-sm">
                        <div className="text-center fw-medium text-primary">
                            Showing findings up to {dateRange.max} days ago
                        </div>
                        <div className="d-flex justify-content-between small text-muted mt-2">
                            <div>
                                {dateRange.max === maxDaysAgo
                                    ? `${dateRange.max} days ago (oldest available)`
                                    : `${dateRange.max} days ago`}
                            </div>
                            <div>Today (0 days ago)</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main content - split into two columns */}
            <Row className="gx-4">
                {/* Left column - Body Silhouette */}
                <Col md={4}>
                    <div className="rounded p-4 position-relative" style={{ height: '400px', width: 'fit-content' }}>
                        {/* SVG Body Silhouette */}
                        <svg viewBox="0 0 100 100" className="mx-auto h-100">
                            {/* Head */}
                            <circle cx="50" cy="10" r="8" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            {/* Body */}
                            <rect x="43" y="18" width="14" height="30" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            {/* Arms */}
                            <rect x="25" y="18" width="18" height="6" rx="3" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            <rect x="57" y="18" width="18" height="6" rx="3" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            {/* Hands */}
                            <circle cx="25" cy="21" r="3" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            <circle cx="75" cy="21" r="3" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            {/* Legs */}
                            <rect x="43" y="48" width="6" height="30" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            <rect x="51" y="48" width="6" height="30" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            {/* Feet */}
                            <rect x="40" y="78" width="9" height="4" rx="2" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            <rect x="51" y="78" width="9" height="4" rx="2" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.5" />
                            {/* Spine */}
                            <rect x="49" y="25" width="2" height="20" fill="#adb5bd" stroke="#adb5bd" strokeWidth="0.1" />

                            {/* Text Labels for regions */}
                            <text x="50" y="10" fontSize="3" textAnchor="middle" fill="#495057">HEAD</text>
                            <text x="50" y="20" fontSize="3" textAnchor="middle" fill="#495057">NECK</text>
                            <text x="50" y="30" fontSize="3" textAnchor="middle" fill="#495057">CHEST</text>
                            <text x="50" y="40" fontSize="3" textAnchor="middle" fill="#495057">ABDOMEN</text>
                            <text x="50" y="50" fontSize="3" textAnchor="middle" fill="#495057">PELVIS</text>
                            <text x="50" y="35" fontSize="3" textAnchor="middle" fill="#495057">SPINE</text>
                            <text x="25" y="21" fontSize="3" textAnchor="middle" fill="#495057">RUE</text>
                            <text x="75" y="21" fontSize="3" textAnchor="middle" fill="#495057">LUE</text>
                            <text x="43" y="75" fontSize="3" textAnchor="middle" fill="#495057">RLE</text>
                            <text x="58" y="75" fontSize="3" textAnchor="middle" fill="#495057">LLE</text>
                        </svg>

                        {/* Finding markers */}
                        {filteredFindings.map((finding: any, index: number) => {
                            const position = getRegionPosition(finding.region, finding);
                            return (
                                <div
                                    key={`finding-${finding.region}-${finding.finding_text}`}
                                    className="position-absolute translate-middle d-flex align-items-center justify-content-center rounded-circle shadow-sm"
                                    style={{
                                        top: position.top,
                                        left: position.left,
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: getCategoryColor(finding.category),
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s',
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold',
                                        color: 'white'
                                    }}
                                    onClick={() => toggleExpandedFinding(`finding-${finding.region}-${finding.finding_text}`)}
                                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                >
                                    {index + 1}
                                </div>
                            );
                        })}
                    </div>
                </Col>

                {/* Right column - Findings List */}
                <Col md={8}>
                    <Row className="mb-3">
                        <Col>
                            <strong>Radiology Findings</strong>
                        </Col>
                        <Col className="text-right">
                            {
                                patientVisit && latestFileVersions && (
                                    <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} latestFileVersions={latestFileVersions} explainKey={"explain_anatomy"} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} />
                                )
                            }
                        </Col>
                    </Row>
                    {
                        isLoading && (
                            <Row>
                                <Col>
                                    <Alert variant='info'><FontAwesomeIcon icon={faSpinner} spin />{' '}Generating findings...</Alert>
                                </Col>
                            </Row>
                        )
                    }
                    <div className="d-flex flex-column gap-3">
                        {filteredFindings.map((finding: any, index: number) => (
                            <AnatomyFindingSection
                                key={`sec-${finding.region}-${finding.finding_text}`} expandedKey={`finding-${finding.region}-${finding.finding_text}`} expandedFinding={expandedFinding}
                                finding={finding} index={index}
                                patientVisit={patientVisit}
                                feedbacks={feedbacks}
                                setFeedbacks={setFeedbacks}
                                scanMulti={scanMulti}
                            />
                        ))}
                        {
                            filteredFindings.length === 0 && (
                                <>
                                    <Row className="text-center text-muted mt-4">
                                        <Col>
                                            <FontAwesomeIcon icon={faBolt} size="2x" />
                                        </Col>
                                    </Row>
                                    <Row className="text-center text-muted">
                                        <Col>
                                            No Data Available
                                        </Col>
                                    </Row>
                                </>
                            )
                        }
                    </div>
                </Col>
            </Row>
        </Container>
    );
};
